/**
 * Contrato zod de las tareas pendientes de trazar (polling al ERP ACSIS).
 *
 * El backend (everex-traza-back · GET /api/despacho/pendientes) consulta
 * `tareas.json?tipo=TAE&solopendientes=1` en Plataforma ACSIS y clasifica cada
 * tarea por `claseComprobante`:
 *   75 → remito_venta · 110 → destruccion · 112 → transferencia
 *
 * Ver docs/integraciones/acsis-plataforma.md
 */

import { z } from 'zod';
import { ESTADOS_TRAZA_JOB } from '@/domain/types';

export const TareaTipoSchema = z.enum([
  'remito_venta',
  'transferencia',
  'destruccion',
  'otro',
]);

/**
 * Estado de la última traza intentada sobre la referencia (si existe).
 * - `pending`/`running` → chip "Trazando" en la card.
 * - `error`/`failed`    → chip "Error de traza".
 * - `success` no llega al front: el backend filtra esas tareas.
 */
export const TareaTrazaSchema = z.object({
  id: z.number(),
  jobId: z.number(),
  status: z.enum(ESTADOS_TRAZA_JOB),
  totalItems: z.number().nullable(),
  doneItems: z.number().nullable(),
  errorItems: z.number().nullable(),
  errorMessage: z.string().nullable(),
  finishedAt: z.string().nullable(),
  lastStatusCheckAt: z.string().nullable(),
});

export const TareaPendienteSchema = z.object({
  id: z.string(),
  /** Tipo interno (según claseComprobante) — define el endpoint de detalle. */
  tipo: TareaTipoSchema,
  /** Prefijo de la referencia (RFA / RES / RCO / RVT / DES / —). Es lo que
   *  ACSIS realmente codifica; se muestra en el chip. */
  sigla: z.string(),
  /** Clasificación legible para el chip: "Salida" | "Destrucción". */
  tipoLabel: z.string(),
  /** Clasificación de movimiento por prefijo de referencia — para color y el
   *  evento ANMAT. 'salida' por defecto; 'destruccion' solo si el prefijo es DES. */
  movimiento: z.enum(['salida', 'destruccion']).optional(),
  claseComprobante: z.string().nullable(),
  /** Referencia del comprobante en ACSIS (ej. "1-1-RFA-43718"). */
  referencia: z.string().nullable(),
  /** Nº de remito en formato AFIP (ej. "R-0001-00043718"), parseado server-side
   *  desde `referencia`. Null si la referencia no tiene el shape esperado. */
  numeroRemito: z.string().nullable(),
  entidad: z.string().nullable(),
  asunto: z.string().nullable(),
  descripcion: z.string().nullable(),
  prioridad: z.string().nullable(),
  solicitante: z.string().nullable(),
  nombreSolicitante: z.string().nullable(),
  responsable: z.string().nullable(),
  nombreResponsable: z.string().nullable(),
  fechaCreacion: z.string().nullable(),
  fechaVencimiento: z.string().nullable(),
  fechaAsignacion: z.string().nullable(),
  /** Estado de la última traza intentada (null = sin intentar). */
  traza: TareaTrazaSchema.nullable().optional(),
});

export const GetTareasPendientesResponse = z.array(TareaPendienteSchema);

export type TareaTipo = z.infer<typeof TareaTipoSchema>;
export type TareaPendiente = z.infer<typeof TareaPendienteSchema>;
export type TareaTraza = z.infer<typeof TareaTrazaSchema>;
